import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cloud, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background - matching app background */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(0,242,234,0.20),transparent_28%),radial-gradient(circle_at_80%_10%,rgba(255,0,128,0.16),transparent_24%),linear-gradient(135deg,#050505,#111827_45%,#020617)]" />
      <div className="fixed inset-0 bg-black/60" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass rounded-3xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-[#00f2ea]/20 to-transparent">
              <Cloud className="w-8 h-8 text-[#00f2ea]" />
            </div>
            <h1 className="text-3xl font-black tracking-tight">TheForecastTracker</h1>
          </div>

          <h2 className="text-xl font-semibold mb-2 text-center">Welcome back</h2>
          <p className="text-white/50 text-center mb-8">Sign in to your account</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="bg-white/5 border-white/10 h-12 rounded-xl focus:border-[#00f2ea]/50 focus:ring-[#00f2ea]/20"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#00f2ea] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="bg-white/5 border-white/10 h-12 rounded-xl pr-12 focus:border-[#00f2ea]/50 focus:ring-[#00f2ea]/20"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle-button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="text-center mt-6 text-white/50">
            Don't have an account?{" "}
            <Link 
              to="/register" 
              className="text-[#00f2ea] hover:underline font-medium"
              data-testid="register-link"
            >
              Sign up
            </Link>
          </p>

          {/* Policy Links */}
          <div className="flex justify-center gap-3 mt-8 text-xs text-white/30">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-white/60 transition-colors">Contact</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
